/* eslint-disable jsx-a11y/heading-has-content */
import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import { LayoutGroup, motion } from 'framer-motion'
import React, { forwardRef, useId } from 'react'
import { TouchTarget } from './button'
import { Link } from './link'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import { useEnvironment } from '../contexts/EnvironmentContext'

export function Sidebar({ className, isCollapsed, onCollapseChange, ...props }) {
  const { isDemoEnvironment } = useEnvironment();

  return (
    <nav
      {...props}
      className={clsx(
        className,
        'flex h-full min-h-0 flex-col bg-azul-surelia overflow-hidden',
        isCollapsed ? 'w-16' : 'w-64',
        isDemoEnvironment && 'pt-[28px]'
      )}
    >
      {isCollapsed && (
        <button
          onClick={onCollapseChange}
          className="lg:hidden w-full p-2 hover:bg-white/10 dark:hover:bg-white/10 transition-colors border-b border-white/20 dark:border-white/20"
          aria-label="Expand sidebar"
        >
          <ChevronRightIcon className="w-4 h-4 text-white/80 mx-auto" />
        </button>
      )}
      {props.children}
    </nav>
  )
}

// `light` wraps the logo in a white rounded fill (for org logos designed for
// light backgrounds), framed by the sidebar's blue — the header itself, the
// collapse button and the borders keep the normal Surelia look.
export function SidebarHeader({ className, isCollapsed, onCollapseChange, light, children, ...props }) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-col border-b border-white/20 p-4 dark:border-white/20 [&>[data-slot=section]+[data-slot=section]]:mt-2.5',
        isCollapsed ? 'items-center' : ''
      )}
    >
      <div className="flex items-start justify-between">
        {light && !isCollapsed ? (
          <div className="min-w-0 flex-1 rounded-lg bg-white px-3 py-2">{children}</div>
        ) : (
          children
        )}
        <button
          onClick={onCollapseChange}
          className={clsx(
            "hidden lg:block p-1.5 rounded-lg bg-white/10 hover:bg-white/20 dark:hover:bg-white/20 transition-colors border border-white/20 dark:border-white/20",
            !isCollapsed && "ml-2"
          )}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronRightIcon className="w-4 h-4 text-white/80" />
          ) : (
            <ChevronLeftIcon className="w-4 h-4 text-white/80" />
          )}
        </button>
      </div>
    </div>
  )
}

export function SidebarBody({ className, isCollapsed, ...props }) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-1 flex-col overflow-y-auto overflow-x-hidden p-4 [&>[data-slot=section]+[data-slot=section]]:mt-8',
        isCollapsed ? 'items-center' : ''
      )}
    />
  )
}

export function SidebarFooter({ className, isCollapsed, ...props }) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-col border-t border-white/20 p-4 dark:border-white/20 [&>[data-slot=section]+[data-slot=section]]:mt-2.5',
        isCollapsed ? 'items-center' : ''
      )}
    />
  )
}

export function SidebarSection({ className, isCollapsed, ...props }) {
  let id = useId()

  return (
    <LayoutGroup id={id}>
      <div 
        {...props} 
        data-slot="section" 
        className={clsx(
          className, 
          'flex flex-col gap-0.5',
          isCollapsed ? 'items-center' : ''
        )} 
      />
    </LayoutGroup>
  )
}

export function SidebarDivider({ className, isCollapsed, ...props }) {
  return (
    <hr 
      {...props} 
      className={clsx(
        className, 
        'my-4 border-t border-zinc-950/5 lg:-mx-4 dark:border-white/5',
        isCollapsed ? 'w-8' : ''
      )} 
    />
  )
}

export function SidebarSpacer({ className, isCollapsed, ...props }) {
  return (
    <div 
      aria-hidden="true" 
      {...props} 
      className={clsx(className, 'mt-8 flex-1')} 
    />
  )
}

export function SidebarHeading({ className, isCollapsed, ...props }) {
  return (
    <h3 
      {...props} 
      className={clsx(
        className, 
        'mb-1 px-2 text-xs/6 font-medium text-zinc-500 dark:text-zinc-400',
        isCollapsed ? 'hidden' : ''
      )} 
    />
  )
}

export const SidebarItem = forwardRef(function SidebarItem(
  { current, className, children, isCollapsed, ...props },
  ref
) {
  let classes = clsx(
    // Base
    'flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-base/6 font-medium text-white sm:py-2 sm:text-sm/5',
    // Leading icon/icon-only
    'data-[slot=icon]:*:size-6 data-[slot=icon]:*:shrink-0 data-[slot=icon]:*:fill-white/80 sm:data-[slot=icon]:*:size-5',
    // Trailing icon (down chevron or similar)
    'data-[slot=icon]:last:*:ml-auto data-[slot=icon]:last:*:size-5 sm:data-[slot=icon]:last:*:size-4',
    // Avatar
    'data-[slot=avatar]:*:-m-0.5 data-[slot=avatar]:*:size-7 data-[slot=avatar]:*:[--ring-opacity:10%] sm:data-[slot=avatar]:*:size-6',
    // Hover
    'data-[hover]:bg-white/10 data-[slot=icon]:*:data-[hover]:fill-white',
    // Active
    'data-[active]:bg-white/15 data-[slot=icon]:*:data-[active]:fill-white',
    // Current (should override hover)
    'data-[current]:text-verde-lima data-[slot=icon]:*:data-[current]:fill-verde-lima data-[current]:data-[hover]:bg-white/15 data-[slot=icon]:*:data-[current]:data-[hover]:fill-verde-lima',
    // Dark mode
    'dark:text-white dark:data-[slot=icon]:*:fill-white/80',
    'dark:data-[hover]:bg-white/10 dark:data-[slot=icon]:*:data-[hover]:fill-white',
    'dark:data-[active]:bg-white/15 dark:data-[slot=icon]:*:data-[active]:fill-white',
    'dark:data-[current]:text-verde-lima dark:data-[slot=icon]:*:data-[current]:fill-verde-lima dark:data-[current]:data-[hover]:bg-white/15 dark:data-[slot=icon]:*:data-[current]:data-[hover]:fill-verde-lima',
    // Collapsed state
    isCollapsed ? 'justify-center' : ''
  );

  return (
    <span className={clsx(className, 'relative')}>
      {current && (
        <motion.span
          layoutId="current-indicator"
          className={clsx(
            "absolute inset-y-2 w-0.5 rounded-full bg-verde-lima dark:bg-verde-lima",
            isCollapsed ? "-left-0" : "-left-4"
          )}
        />
      )}
      {'href' in props ? (
        <Headless.CloseButton
          as={Link}
          {...props}
          className={classes}
          data-current={current ? 'true' : undefined}
          ref={ref}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.CloseButton>
      ) : (
        <Headless.Button
          {...props}
          className={clsx('cursor-default', classes)}
          data-current={current ? 'true' : undefined}
          ref={ref}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      )}
    </span>
  )
})

export function SidebarLabel({ className, isCollapsed, ...props }) {
  return (
    <span 
      {...props} 
      className={clsx(
        className, 
        'truncate',
        isCollapsed ? 'hidden' : ''
      )} 
    />
  )
}
